import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Clock, ArrowRight, Star, TrendingUp, Heart, ShieldCheck, LayoutGrid, List, Rows, Grid3x3, Lock } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import GuestHeader from "@/components/GuestHeader";
import { useBranding } from "@/lib/branding";
import { getGuest } from "@/lib/guest";
import { readFresh, readAny, prime, prefetchImage, startGuestBackgroundSync } from "@/lib/prefetch";
import { prewarmBlobImages } from "@/lib/imageBlobCache";
import BlobImage from "@/components/BlobImage";
import { useGuestSessionWatch } from "@/lib/useGuestSessionWatch";
import { useIpadLive } from "@/lib/useIpadLive";
import { useAuth } from "@/context/AuthContext";
import PendingInviteChip from "@/components/PendingInviteChip";
import GuestNotificationChip from "@/components/GuestNotificationChip";
import { GenderAvailabilityIcons } from "@/components/GenderIcons";
import TherapistAvatars from "@/components/TherapistAvatars";
import GuestHealthDialog from "./GuestHealthDialog";
import { ChevronDown } from "lucide-react";
import { useTrackView } from "@/lib/analyticsTracker";

const FALLBACK_IMG = "https://images.unsplash.com/photo-1600334089648-b0d9d3028eb2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function GuestMenu() {
  useGuestSessionWatch();
  useTrackView("menu");
  const { id: guestIdFromStore } = getGuest();
  // Two-tier hydration: in-memory prefetch first, then localStorage
  // fallback (`readAny`). The localStorage layer keeps tiles + photo
  // URLs from the *previous* session so a returning iPad paints
  // instantly on hard refresh instead of showing a spinner while the
  // network catches up. The fetch below then quietly revalidates.
  const [treatments, setTreatments] = useState(() => readAny("/public/treatments") || []);
  const [crew, setCrew] = useState(() => readAny("/public/crew") || []);
  const [categories, setCategories] = useState(() => readAny("/public/treatment-categories") || []);
  const [settings, setSettings] = useState(() => readAny("/public/settings") || { show_treatment_ratings: false, show_treatment_popularity: false });
  const [loading, setLoading] = useState(() => !readAny("/public/treatments"));
  // Wellness questionnaire — fetched once so we can render either a "✓ saved"
  // chip or a discreet nudge, and gate the first booking attempt.
  const [health, setHealth] = useState(null);
  const [healthOpen, setHealthOpen] = useState(false);
  const [pendingBookTreatment, setPendingBookTreatment] = useState(null);
  // Fold/unfold state per category. Empty object = every category starts folded,
  // so guests scan headings first and expand only what interests them.
  const [openCategories, setOpenCategories] = useState({});
  // Density mode — 'tiles' (dense image tile grid, default — packs 4-5 per row
  // on desktop with just image + name + duration + therapist avatars), 'grid'
  // (large hero cards with description + rating), 'list' (row-oriented, image
  // left / text right) or 'compact' (dense one-liners). Persisted per device
  // so returning guests keep their preference.
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem("guest_menu_view_v1") || "tiles"; } catch { return "tiles"; }
  });
  useEffect(() => { try { localStorage.setItem("guest_menu_view_v1", viewMode); } catch { /* ignore */ } }, [viewMode]);
  const navigate = useNavigate();
  const { name: guestName, female: guestFemale } = getGuest();
  const { boatName } = useBranding();
  // Badge visibility is driven solely by the Manager+ setting toggles — that's
  // the whole point of the switches. Dashboard cookies leaking into the guest
  // menu view would surprise operators who just turned them off.
  const showRatings = !!settings.show_treatment_ratings;
  const showPopularity = !!settings.show_treatment_popularity;

  useEffect(() => {
    if (!guestName) { navigate("/"); return; }
    (async () => {
      try {
        const [tr, cr, cat, st] = await Promise.all([
          api.get("/public/treatments"),
          api.get("/public/crew"),
          api.get("/public/treatment-categories"),
          api.get("/public/settings"),
        ]);
        setTreatments(tr.data || []);
        setCrew(cr.data || []);
        setCategories(cat.data || []);
        setSettings(st.data || {});
        // Warm the treatment + crew photos in the browser memory-cache
        // so subsequent <img> mounts render from cache. Fires on the
        // response landing rather than image render so open sockets
        // saturate the pipe while React is still building tiles.
        for (const t of (tr.data || [])) if (t.image_path) prefetchImage(t.image_path);
        for (const c of (cr.data || [])) if (c.image_path) prefetchImage(c.image_path);
        // Also stash the bytes in IndexedDB via the blob cache so a
        // hard refresh / device sleep doesn't re-download the same
        // photos. `no-store` from the ingress defeats the HTTP cache;
        // IndexedDB is the only tier that actually persists.
        prewarmBlobImages([
          ...((tr.data || []).map((t) => t.image_path)),
          ...((cr.data || []).map((c) => c.image_path)),
        ]);
      } finally { setLoading(false); }
    })();

    // Start the background revalidator on iPad kiosks so mid-charter
    // content edits reach the kiosk within ~60 s even if the WS drops.
    // Personal phones use `useGuestSessionWatch` for session validity
    // and don't need the periodic re-warm. Cleanup on unmount.
    const stop = startGuestBackgroundSync(60_000);
    // Fetch wellness profile in parallel — soft-fail is fine. The
    // response now carries `roster_linked` so we can suppress the
    // wellness prompt for owners / family / staff whose medical data
    // is already recorded by the crew (regulars flow — see
    // `_guest_health_snapshot` on the backend).
    if (guestIdFromStore) {
      api.get(`/public/guest-health?guest_id=${encodeURIComponent(guestIdFromStore)}`)
        .then(({ data }) => setHealth(data))
        .catch((e) => console.debug("[GuestMenu] health fetch failed:", e));
    }
    return () => { stop && stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Live re-sync on the iPad: when a manager toggles a treatment, edits
  // the crew, changes a category or flips a settings switch, the
  // backend fans out `settings.updated`, `settings.menu_visibility_updated`
  // and `content.updated` events over the iPad WS. We refetch silently —
  // no loader flash, no route change.
  useIpadLive((ev) => {
    if (!ev?.type) return;
    const menuAffectingKinds = new Set(["treatments", "crew", "treatment_categories", "settings"]);
    const isContentEvent = ev.type === "content.updated" && menuAffectingKinds.has(ev.kind);
    if (ev.type === "settings.updated" || ev.type === "settings.menu_visibility_updated" || isContentEvent) {
      Promise.all([
        api.get("/public/treatments"),
        api.get("/public/crew"),
        api.get("/public/treatment-categories"),
        api.get("/public/settings"),
      ]).then(([tr, cr, cat, st]) => {
        setTreatments(tr.data || []);
        setCrew(cr.data || []);
        setCategories(cat.data || []);
        setSettings(st.data || {});
        try {
          prime("/public/treatments", tr.data || []);
          prime("/public/crew", cr.data || []);
          prime("/public/treatment-categories", cat.data || []);
          prime("/public/settings", st.data || {});
        } catch { /* prime is best-effort */ }
        // Warm any newly-added tile photos too so operators uploading
        // a fresh treatment shot see it appear instantly on kiosks.
        for (const t of (tr.data || [])) if (t.image_path) prefetchImage(t.image_path);
        for (const c of (cr.data || [])) if (c.image_path) prefetchImage(c.image_path);
      }).catch(() => {});
    }
  });

  const bookOrPromptHealth = (t) => {
    // Regulars — crew already holds their allergies / conditions /
    // preferences on the roster record, so we NEVER show the wellness
    // dialog for them. Straight to booking.
    if (health?.roster_linked) {
      navigate(`/book/${t.id}`);
      return;
    }
    // Open the questionnaire on the first Reserve of every session — even
    // for guests who submitted answers on a previous visit — so the therapist
    // always has fresh consent that nothing has changed since last time.
    // Previously-submitted guests get a one-tap "Use my saved answers"
    // continue button inside the dialog, so this stays a light friction
    // point rather than a re-typing chore. Once acknowledged we set a
    // sessionStorage flag and skip the gate for the rest of the session.
    let confirmedThisSession = false;
    try { confirmedThisSession = sessionStorage.getItem("yacht_wellness_confirmed_v1") === "1"; } catch { /* private mode */ }
    if (!confirmedThisSession) {
      setPendingBookTreatment(t);
      setHealthOpen(true);
      return;
    }
    navigate(`/book/${t.id}`);
  };

  const markWellnessConfirmed = () => {
    try { sessionStorage.setItem("yacht_wellness_confirmed_v1", "1"); } catch { /* ignore */ }
  };

  const grouped = useMemo(() => {
    // Hide treatments that aren't available for the current guest's gender.
    // Both flags default to true on the backend so historic treatments remain
    // available to everyone — this only filters when a manager explicitly
    // toggles one off.
    const { female } = getGuest();
    const genderVisible = (t) => {
      const male = t.available_for_male !== false;
      const fem = t.available_for_female !== false;
      if (female === true) return fem;
      if (female === false) return male;
      return male || fem; // unknown gender → show if visible to anyone
    };
    const filtered = (treatments || []).filter(genderVisible);
    const byCat = new Map();
    categories.forEach((c) => byCat.set(c.id, { category: c, items: [] }));
    const uncategorized = [];
    filtered.forEach((t) => {
      if (t.category_id && byCat.has(t.category_id)) byCat.get(t.category_id).items.push(t);
      else uncategorized.push(t);
    });
    const groups = Array.from(byCat.values()).filter((g) => g.items.length > 0);
    if (uncategorized.length) groups.push({ category: { id: "_none", name: "Other" }, items: uncategorized });
    return groups;
  }, [treatments, categories]);

  // Adults-only gate. The guest's age lives on the cabin roster (crew-entered
  // during check-in). If under 18 OR crew-flagged as `minor`, any treatment
  // or category flagged `adults_only` becomes visible-but-non-bookable with
  // a polite copy line. Unknown / undeclared ages AND minor=false pass
  // through — we never turn "unknown" into "under 18" on the frontend
  // either. Matches backend rule in `_validate_booking_rules`.
  const guestIsUnderAge = useMemo(() => {
    const { id, roster } = getGuest();
    if (!id || !Array.isArray(roster)) return false;
    const me = roster.find((r) => r.guest_id === id);
    if (!me) return false;
    if (me.minor === true) return true;
    const raw = me.age;
    if (raw === null || raw === undefined || raw === "") return false;
    const n = Number(raw);
    return Number.isFinite(n) && n < 18;
  }, []);
  const categoriesById = useMemo(() => {
    const m = new Map();
    (categories || []).forEach((c) => m.set(c.id, c));
    return m;
  }, [categories]);
  const isAdultBlocked = (t) => {
    if (!guestIsUnderAge) return false;
    if (t?.adults_only) return true;
    const cat = t?.category_id ? categoriesById.get(t.category_id) : null;
    return !!(cat && cat.adults_only);
  };
  const adultsOnlyMsg = (settings?.adults_only_message || "").trim()
    || "Unfortunately this treatment is not proper for below 18 years old.";
  const handleTreatmentTap = (t) => {
    if (isAdultBlocked(t)) {
      toast.error(adultsOnlyMsg);
      return;
    }
    bookOrPromptHealth(t);
  };

  return (
    <div className="min-h-screen bg-background">
      <GuestHeader guestName={guestName} />

      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-8">
        <div className="overline text-accent mb-6" data-testid="menu-title">The Menu</div>
        <h1 className="font-display text-5xl md:text-7xl tracking-tight leading-[1.05] max-w-3xl">
          Rituals crafted for<br /><em className="text-accent font-normal">quiet waters.</em>
        </h1>
        <p className="mt-6 max-w-xl text-muted-foreground text-lg">
          Welcome, {guestName}. Select a treatment to begin your reservation.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 py-12">
        {loading ? (
          <div className="text-muted-foreground">Loading treatments…</div>
        ) : treatments.length === 0 ? (
          <div className="border border-border p-10 text-muted-foreground" data-testid="empty-treatments">
            The spa menu is being prepared. Please check back shortly.
          </div>
        ) : (
          <>
          {/* Wellness questionnaire status — a subtle affordance either way.
              After first save the chip becomes a passive "✓ saved" pill that
              still opens the dialog when tapped so guests can revise answers. */}
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
            <div className="space-y-2">
              {health?.roster_linked ? (
                // Regulars — the wellness data lives on the roster
                // record and is managed by the crew. No self-service
                // questionnaire, no nudge — just a discreet chip so
                // the guest knows their preferences are on file.
                <div
                  className="inline-flex items-center gap-2 px-3 py-2 border border-accent/40 bg-accent/5 text-accent text-sm"
                  data-testid="guest-health-status-chip-regular"
                >
                  <ShieldCheck className="h-4 w-4" />
                  <span>Your preferences are on file with the crew — no wellness form needed</span>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setHealthOpen(true)}
                  className={`inline-flex items-center gap-2 px-3 py-2 border text-sm transition-colors ${
                    health?.submitted
                      ? "border-accent/40 bg-accent/5 text-accent hover:bg-accent/10"
                      : "border-dashed border-accent/50 bg-accent/5 text-foreground hover:border-accent hover:bg-accent/10"
                  }`}
                  data-testid="guest-health-status-chip"
                >
                  {health?.submitted ? (
                    <>
                      <ShieldCheck className="h-4 w-4" />
                      <span>Wellness preferences saved — tap to update</span>
                    </>
                  ) : (
                    <>
                      <Heart className="h-4 w-4 text-accent" />
                      <span>Please share your wellness preferences before your first treatment</span>
                    </>
                  )}
                </button>
              )}
              <GuestNotificationChip guestId={guestIdFromStore} />
              <PendingInviteChip guestId={guestIdFromStore} />
            </div>
            {/* Density toggle — tiles / grid / list / compact. Persisted per device. */}
            <div className="inline-flex border border-border bg-card/50" role="group" aria-label="Menu density" data-testid="menu-view-toggle">
              {[
                { id: "tiles", icon: Grid3x3, label: "Tiles" },
                { id: "grid", icon: LayoutGrid, label: "Grid" },
                { id: "list", icon: List, label: "List" },
                { id: "compact", icon: Rows, label: "Compact" },
              ].map(({ id, icon: Icon, label }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setViewMode(id)}
                  aria-pressed={viewMode === id}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs uppercase tracking-widest transition-colors ${viewMode === id ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                  data-testid={`menu-view-${id}`}
                  title={`${label} view`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">{label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-16" data-testid="treatments-grid">
            {grouped.map((group) => {
              const open = !!openCategories[group.category.id];
              return (
              <div key={group.category.id} data-testid={`category-group-${group.category.id}`}>
                <button
                  type="button"
                  onClick={() => setOpenCategories((s) => ({ ...s, [group.category.id]: !open }))}
                  aria-expanded={open}
                  className="w-full flex items-center gap-4 mb-6 border-b border-border pb-3 text-left hover:bg-muted/30 transition-colors group"
                  data-testid={`category-toggle-${group.category.id}`}
                >
                  <ChevronDown className={`h-5 w-5 text-primary/50 transition-transform ${open ? "rotate-0" : "-rotate-90"}`} strokeWidth={1.5} />
                  <h2 className="font-display text-3xl tracking-tight">{group.category.name}</h2>
                  <span className="overline text-primary/40 text-[10px]">{group.items.length} {group.items.length === 1 ? "treatment" : "treatments"}</span>
                  <span className="ml-auto overline text-primary/40 text-[10px] group-hover:text-accent transition-colors">{open ? "Hide" : "Show"}</span>
                </button>
                {/* Keep category content mounted even when folded so the
                    treatment <img> tags never unmount and re-fetch. The
                    ingress serves /api/files with `no-store`, so any
                    remount would trigger a fresh network round-trip
                    even though the pixels sit in RAM. `hidden` collapses
                    the box without dropping the React subtree. */}
                <div hidden={!open}>
                {viewMode === "tiles" && (
                  <div
                    className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4"
                    data-testid={`tiles-view-${group.category.id}`}
                  >
                    {group.items.map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => handleTreatmentTap(t)}
                        className={`group relative bg-card border border-border overflow-hidden hover:border-accent transition-all text-left flex flex-col ${isAdultBlocked(t) ? "opacity-70 hover:border-border" : ""}`}
                        data-testid={`treatment-card-${t.id}`}
                      >
                        <div className="relative aspect-square bg-muted overflow-hidden">
                          <BlobImage
                            path={t.image_path || null}
                            src={FALLBACK_IMG}
                            alt={t.name}
                            loading="lazy"
                            decoding="async"
                            className="w-full h-full object-cover img-zoom"
                            onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }}
                          />
                          {/* Duration pill (top-right) keeps the tile self-describing
                              without adding a second text row and stealing image space. */}
                          <span className="absolute top-2 right-2 bg-background/85 backdrop-blur px-2 py-0.5 text-[10px] font-mono tracking-wider">
                            {t.duration_min}′
                          </span>
                          {isAdultBlocked(t) && (
                            <span
                              className="absolute top-2 left-2 inline-flex items-center gap-1 bg-primary/90 text-primary-foreground px-2 py-0.5 text-[10px] uppercase tracking-widest"
                              data-testid={`adults-only-badge-${t.id}`}
                            >
                              <Lock className="h-2.5 w-2.5" /> 18+
                            </span>
                          )}
                        </div>
                        <div className="p-3 flex-1 flex flex-col gap-2">
                          <h3 className="font-display text-sm sm:text-base leading-tight tracking-tight line-clamp-2">
                            {t.name}
                          </h3>
                          {isAdultBlocked(t) && (
                            <p className="text-[10px] italic text-muted-foreground -mt-1" data-testid={`adults-only-note-${t.id}`}>
                              {adultsOnlyMsg}
                            </p>
                          )}
                          {/* Bottom row = therapist avatars left, subtle arrow right.
                              Avatar row has fixed height so tile geometry stays
                              consistent whether the treatment has assigned crew or not. */}
                          <div className="mt-auto flex items-center justify-between gap-2 pt-1 min-h-[24px]">
                            {crew.length > 0 && (t.crew_ids || []).length > 0 ? (
                              <TherapistAvatars
                                crewIds={t.crew_ids || []}
                                allCrew={crew}
                                size={22}
                                max={3}
                                testIdPrefix={`treatment-${t.id}-`}
                              />
                            ) : <span />}
                            <ArrowRight className="h-3.5 w-3.5 text-accent shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {viewMode === "grid" && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
                  {group.items.map((t, idx) => (
                    <motion.article
                      key={t.id}
                      initial={{ opacity: 0, y: 24 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: idx * 0.04, ease: "easeOut" }}
                      className="group hover-lift bg-card border border-border overflow-hidden flex flex-col h-full"
                      data-testid={`treatment-card-${t.id}`}
                    >
                      <div className="relative aspect-[4/3] overflow-hidden bg-muted">
                        <BlobImage
                          path={t.image_path || null}
                          src={FALLBACK_IMG}
                          alt={t.name}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-cover img-zoom"
                          onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }}
                        />
                        {isAdultBlocked(t) && (
                          <span
                            className="absolute top-3 left-3 inline-flex items-center gap-1 bg-primary/90 text-primary-foreground px-2.5 py-1 text-[11px] uppercase tracking-widest"
                            data-testid={`adults-only-badge-${t.id}`}
                          >
                            <Lock className="h-3 w-3" /> 18+ Adults only
                          </span>
                        )}
                      </div>
                      <div className="p-8 flex flex-col flex-1">
                        <div className="flex items-baseline justify-between gap-4 mb-3">
                          <h3 className="font-display text-2xl tracking-tight">{t.name}</h3>
                          {!t.price_hidden && <span className="font-display text-xl text-accent">€{Number(t.price).toFixed(0)}</span>}
                        </div>
                        <div className="flex flex-wrap items-center gap-3 text-muted-foreground text-sm mb-4">
                          <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {t.duration_min} min</span>
                          {t.couples_allowed && <span className="overline text-[10px] text-accent">· Couples</span>}
                          <GenderAvailabilityIcons
                            male={t.available_for_male !== false}
                            female={t.available_for_female !== false}
                            size={13}
                            className="ml-auto"
                            testId={`gender-icons-${t.id}`}
                          />
                        </div>
                        {isAdultBlocked(t) && (
                          <p className="text-xs text-muted-foreground italic mb-3" data-testid={`adults-only-note-${t.id}`}>
                            {adultsOnlyMsg}
                          </p>
                        )}
                        {/* Compact overlapping therapist avatars — pulled from `t.crew_ids`.
                            Fixed height keeps card geometry stable whether there's 1 or
                            5 thumbnails. Hidden entirely when no crew have been added yet. */}
                        {crew.length > 0 && (
                          <div className="h-8 mb-4 flex items-center">
                            <TherapistAvatars
                              crewIds={t.crew_ids || []}
                              allCrew={crew}
                              size={28}
                              max={5}
                              testIdPrefix={`treatment-${t.id}-`}
                            />
                          </div>
                        )}
                        {t.description && (
                          <p className="text-sm text-foreground/70 leading-relaxed line-clamp-3 mb-6">{t.description}</p>
                        )}
                        {/* Bottom row is pinned to the bottom of every card so cards
                            look uniform regardless of description length. */}
                        <div className="mt-auto pt-2 flex items-end justify-between gap-3">
                          <Button
                            onClick={() => handleTreatmentTap(t)}
                            data-testid={`book-btn-${t.id}`}
                            disabled={isAdultBlocked(t)}
                            className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground disabled:opacity-60"
                          >
                            {isAdultBlocked(t) ? "Adults only" : "Reserve"} {!isAdultBlocked(t) && <ArrowRight className="ml-2 h-4 w-4" />}
                          </Button>
                          {(showRatings || showPopularity) && (
                            <div className="text-right leading-tight self-end" data-testid={`treatment-badge-${t.id}`}>
                              {showRatings && (
                                <div className="text-[11px]">
                                  {t.rating && t.rating.count > 0 ? (
                                    <span className="inline-flex items-center gap-1 text-accent" data-testid={`rating-badge-${t.id}`}>
                                      <Star className="h-3 w-3 fill-accent" strokeWidth={0} />
                                      <span className="font-mono">{t.rating.avg.toFixed(1)}</span>
                                      <span className="text-muted-foreground text-[10px]">· {t.rating.count} {t.rating.count === 1 ? "review" : "reviews"}</span>
                                    </span>
                                  ) : (
                                    <span className="text-muted-foreground italic text-[10px]" data-testid={`rating-empty-${t.id}`}>
                                      Awaiting first ripple
                                    </span>
                                  )}
                                </div>
                              )}
                              {showPopularity && t.popularity && (
                                <div className="text-[10px] text-muted-foreground mt-0.5 flex items-center gap-1 justify-end" data-testid={`popularity-badge-${t.id}`}>
                                  <TrendingUp className="h-2.5 w-2.5" />
                                  <span>Chosen {t.popularity.count}× {t.popularity.period_label}</span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.article>
                  ))}
                </div>
                )}
                {viewMode === "list" && (
                <div className="flex flex-col gap-4" data-testid={`list-view-${group.category.id}`}>
                  {group.items.map((t, idx) => (
                    <motion.article
                      key={t.id}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.35, delay: idx * 0.03 }}
                      className="group hover-lift bg-card border border-border overflow-hidden flex flex-col sm:flex-row"
                      data-testid={`treatment-card-${t.id}`}
                    >
                      <div className="relative sm:w-56 md:w-60 sm:shrink-0 aspect-[4/3] sm:aspect-square overflow-hidden bg-muted">
                        <BlobImage
                          path={t.image_path || null}
                          src={FALLBACK_IMG}
                          alt={t.name}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-cover img-zoom"
                          onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }}
                        />
                        {isAdultBlocked(t) && (
                          <span
                            className="absolute top-2 left-2 inline-flex items-center gap-1 bg-primary/90 text-primary-foreground px-2 py-0.5 text-[10px] uppercase tracking-widest"
                            data-testid={`adults-only-badge-${t.id}`}
                          >
                            <Lock className="h-2.5 w-2.5" /> 18+
                          </span>
                        )}
                      </div>
                      <div className="p-5 sm:p-6 flex flex-col flex-1 min-w-0">
                        <div className="flex items-baseline justify-between gap-4 mb-2">
                          <h3 className="font-display text-xl md:text-2xl tracking-tight truncate">{t.name}</h3>
                          {!t.price_hidden && <span className="font-display text-lg md:text-xl text-accent shrink-0">€{Number(t.price).toFixed(0)}</span>}
                        </div>
                        <div className="flex flex-wrap items-center gap-3 text-muted-foreground text-sm mb-3">
                          <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {t.duration_min} min</span>
                          {t.couples_allowed && <span className="overline text-[10px] text-accent">· Couples</span>}
                          <GenderAvailabilityIcons
                            male={t.available_for_male !== false}
                            female={t.available_for_female !== false}
                            size={13}
                            testId={`gender-icons-${t.id}`}
                          />
                        </div>
                        {isAdultBlocked(t) && (
                          <p className="text-xs text-muted-foreground italic mb-3" data-testid={`adults-only-note-${t.id}`}>
                            {adultsOnlyMsg}
                          </p>
                        )}
                        {t.description && (
                          <p className="text-sm text-foreground/70 leading-relaxed line-clamp-2 mb-4">{t.description}</p>
                        )}
                        <div className="mt-auto flex items-center justify-between gap-3">
                          <div className="flex items-center gap-4 min-w-0">
                            {crew.length > 0 && (
                              <TherapistAvatars
                                crewIds={t.crew_ids || []}
                                allCrew={crew}
                                size={24}
                                max={4}
                                testIdPrefix={`treatment-${t.id}-`}
                              />
                            )}
                            {showRatings && t.rating && t.rating.count > 0 && (
                              <span className="inline-flex items-center gap-1 text-accent text-[11px]" data-testid={`rating-badge-${t.id}`}>
                                <Star className="h-3 w-3 fill-accent" strokeWidth={0} />
                                <span className="font-mono">{t.rating.avg.toFixed(1)}</span>
                              </span>
                            )}
                          </div>
                          <Button
                            onClick={() => handleTreatmentTap(t)}
                            data-testid={`book-btn-${t.id}`}
                            disabled={isAdultBlocked(t)}
                            className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground shrink-0 disabled:opacity-60"
                          >
                            {isAdultBlocked(t) ? "Adults only" : "Reserve"} {!isAdultBlocked(t) && <ArrowRight className="ml-2 h-4 w-4" />}
                          </Button>
                        </div>
                      </div>
                    </motion.article>
                  ))}
                </div>
                )}
                {viewMode === "compact" && (
                <div className="border border-border divide-y divide-border bg-card" data-testid={`compact-view-${group.category.id}`}>
                  {group.items.map((t) => (
                    <div
                      key={t.id}
                      className="flex items-center gap-4 px-4 sm:px-5 py-3 hover:bg-muted/30 transition-colors"
                      data-testid={`treatment-card-${t.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-display text-base sm:text-lg tracking-tight truncate flex items-center gap-2">
                          {t.name}
                          {isAdultBlocked(t) && (
                            <span
                              className="inline-flex items-center gap-1 bg-primary/90 text-primary-foreground px-1.5 py-0.5 text-[9px] uppercase tracking-widest shrink-0"
                              data-testid={`adults-only-badge-${t.id}`}
                            >
                              <Lock className="h-2.5 w-2.5" /> 18+
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-muted-foreground flex items-center gap-3 mt-0.5 flex-wrap">
                          <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{t.duration_min} min</span>
                          {t.couples_allowed && <span className="overline text-[9px] text-accent">Couples</span>}
                          <GenderAvailabilityIcons
                            male={t.available_for_male !== false}
                            female={t.available_for_female !== false}
                            size={11}
                            testId={`gender-icons-${t.id}`}
                          />
                          {showRatings && t.rating && t.rating.count > 0 && (
                            <span className="inline-flex items-center gap-1 text-accent" data-testid={`rating-badge-${t.id}`}>
                              <Star className="h-2.5 w-2.5 fill-accent" strokeWidth={0} />
                              <span className="font-mono">{t.rating.avg.toFixed(1)}</span>
                            </span>
                          )}
                        </div>
                      </div>
                      {!t.price_hidden && <span className="font-display text-base sm:text-lg text-accent shrink-0">€{Number(t.price).toFixed(0)}</span>}
                      <Button
                        size="sm"
                        onClick={() => handleTreatmentTap(t)}
                        data-testid={`book-btn-${t.id}`}
                        disabled={isAdultBlocked(t)}
                        className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground shrink-0 disabled:opacity-60"
                      >
                        {isAdultBlocked(t) ? "Adults only" : "Reserve"} {!isAdultBlocked(t) && <ArrowRight className="ml-1.5 h-3.5 w-3.5" />}
                      </Button>
                    </div>
                  ))}
                </div>
                )}
                </div>
              </div>
              );
            })}
          </div>
          </>
        )}
      </section>

      {crew.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-10 py-20 border-t border-border">
          <div className="overline text-accent mb-4">Your Crew</div>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight mb-12">The hands behind the ritual.</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8" data-testid="crew-grid">
            {crew.map((c) => (
              <div key={c.id} className="group" data-testid={`crew-card-${c.id}`}>
                <div className="aspect-[3/4] overflow-hidden bg-muted mb-4">
                  {c.image_path ? (
                    <BlobImage path={c.image_path} alt={c.name} loading="lazy" decoding="async" className="w-full h-full object-cover img-zoom" />
                  ) : (
                    <div className="w-full h-full bg-muted flex items-center justify-center text-muted-foreground text-sm">No photo</div>
                  )}
                </div>
                <h4 className="font-display text-xl">{c.name}</h4>
                <div className="overline text-primary/50 mt-1">{c.role}</div>
                {c.bio && <p className="text-sm text-foreground/70 mt-2 line-clamp-2">{c.bio}</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      <footer className="border-t border-border py-10 text-center overline text-muted-foreground">
        {boatName} · Onboard Spa
      </footer>
      <GuestHealthDialog
        open={healthOpen}
        onOpenChange={(v) => {
          setHealthOpen(v);
          if (!v) {
            // Any explicit dismiss ("Not now" or the ✕) counts as an
            // acknowledgement for the rest of the session — otherwise
            // the dialog would re-open on every Reserve click.
            markWellnessConfirmed();
            if (pendingBookTreatment) {
              const t = pendingBookTreatment;
              setPendingBookTreatment(null);
              setTimeout(() => navigate(`/book/${t.id}`), 200);
            }
          }
        }}
        guestId={guestIdFromStore}
        female={!!guestFemale}
        onSaved={(profile) => {
          setHealth(profile);
          markWellnessConfirmed();
          if (pendingBookTreatment) {
            const t = pendingBookTreatment;
            setPendingBookTreatment(null);
            setTimeout(() => navigate(`/book/${t.id}`), 250);
          }
        }}
      />
    </div>
  );
}
